/**
 * 详情页 / PDP 规格 —— 与 `platforms.ts` 配套的第七件事。
 *
 * 为什么单独一个文件：
 * `platforms.ts` 已经背了六件事（语言 / 尺寸 / 结构 / 卖点框架 / 风格 / 禁词），
 * 再塞进「17 个平台 × 8 个详情模块」会到一千多行，改平台要翻半天。
 * 但**真源仍然只有一份**：这里定义形态族，`platforms.ts` 里每个平台只挂一个引用
 * （`detail: DETAIL_SPECS.AMAZON_APLUS_BASIC`），同族平台共用一套规格，不存在第二份拷贝。
 *
 * ---------------------------------------------------------------------------
 * 这张表解决的真实问题（2026-09-29 用户报障 + 官方规格复核）
 * ---------------------------------------------------------------------------
 * 原实现把「亚马逊 A+」写死成**统一 16:9**，六张 A+ 全按一个横版比例出图。
 * 而 Amazon A+ 的模块尺寸**逐模块不同**，且**分两个档位、两档比例不通用**。
 *
 * 官方规格（Seller Central「A+ 商品描述指南」+ Premium A+ 模块指南，
 * 2026-09-29 复核，多源一致）：
 *
 *   基本 A+（Basic，14 个可选模块，**一个详情页最多 5 个模块**）
 *     Company Logo .................. 600 × 180
 *     Image Header with Text ........ 970 × 600  ← 基本档的 hero，1.62:1
 *     Image & Dark/Light Text Overlay  970 × 300
 *     Three Images & Text ........... 300 × 300 ×3
 *     Single Left / Right Image ..... 300 × 300
 *     Single Image & Sidebar ........ 300 × 400 主图 + 350 × 175 侧栏
 *     Four Images & Text ............ 220 × 220 ×4
 *     Four Image/Text Quadrant ...... 135 × 135 ×4
 *     Comparison Chart .............. 150 × 300 / 个
 *     Technical Specs / Description Text ... 纯文字，无图
 *
 *   高级 A+（Premium，19 个可选模块，**一个详情页最多 7 个模块**）
 *     Full Image / Background with Text / Hotspots / Carousel
 *       ............................ 桌面 1464 × 600   移动 600 × 450
 *     Comparison Table (large) ...... 桌面 488 × 700   移动 600 × 450
 *     Single Image with Text ........ 800 × 600（移动复用桌面图）
 *     Dual Images with Text ......... 650 × 350（移动复用桌面图）
 *     Four Images and Text .......... 300 × 225（移动复用桌面图）
 *
 * 三个必须记住的结论：
 *
 * 1. **两档比例不同，不能互缩。** 基本档 hero 是 970×600（1.62），
 *    高级档全宽是 1464×600（2.44）。按 16:9（1.78）出图两档都对不上；
 *    按高级档出图铺进基本档后台，会横向被压掉 1/3。
 *    → 所以档位是**根决策**，必须在页签外显式选，不能让程序猜。
 *
 * 2. **移动端是单独的一份资产，不是桌面图裁切。**
 *    官方对高级 A+ 的全宽类模块要求分别上传 desktop 与 mobile 两个文件
 *    （mobile = 600 × 450，4:3，而桌面是 2.44:1 —— 比例完全不同，
 *    所以「把 1464×600 裁成 600×450」必然切掉左右主体）。
 *    小的模块（单图/双图/四图）不需要单独移动图，后台复用桌面图。
 *
 * 3. **一个模块可能要好几张图。** 三图模块要 3 张 300×300、四图要 4 张、
 *    对比表最多 6 张、单图+侧栏是 300×400 + 350×175 两种尺寸。
 *    所以模块下面挂的是**图位（slots）**，不是「一个模块 = 一张图」。
 *
 * 好消息是**上游 size 参数支持直接传分辨率**（`ss-art-image-gen` 的
 * `--size 970x600`）。2026-09-29 实测（真提交一张 970x600，见
 * `_probe_aplus_ratio.py`）：上游**严格按该宽高比出图**，拿回的图是
 * 1595×986 —— 宽高比 1.6176，与 970:600 的 1.6167 差 0.06%，
 * 而 16:9 是 1.7778（差 10%）。
 *
 * ⚠️ 别把这句话读成「像素等于官方值」：像素由分辨率档位决定，
 * 固定的是**比例**。真正要杜绝的是「用 16:9 硬凑 970×600」那种近似。
 *
 * 也不靠"用接近的比例生成、再本地裁切"—— 本地裁切会切掉产品主体，
 * 还违反技能「交付图必须由生图工具直接生成」的边界。
 *
 * 平台没有硬尺寸规范的（社媒类），才退回写比例（如 4:5）。
 */
import type { IconName } from '@/components/icons'

/**
 * 一屏在买家决策链里扮演的角色。
 *
 * **定义在这里而不是 `detailPlan.ts`**：它是模块的固有属性（「这一屏是干嘛的」），
 * 与尺寸同级。放那边会造成 `platformDetails ↔ detailPlan` 的循环引用。
 * `detailPlan.ts` 只负责按角色派生该说什么话（买家问题 / 视觉角色 / 构图尺度）。
 *
 * 十个角色覆盖 7 个形态族的全部模块 —— 不是十种页面，是十个**语义标签**：
 * 同一个角色在不同平台可以是不同模块（`craft` 在国内长图叫「材质工艺」，
 * 在 A+ 叫「细节信任」，在社媒图集叫「细节特写」）。
 */
export type DetailBeatRole =
  /** 这是什么、长什么样（白底主图 / PDP 定妆） */
  | 'identity'
  /** 凭什么值得往下看（首屏主视觉） */
  | 'hook'
  /** 我是不是也有这个麻烦（痛点现场） */
  | 'need'
  /** 它凭什么能解决（卖点 / 机制 / 功能证明） */
  | 'proof'
  /** 做工扎实吗（材质 / 工艺 / 结构微距） */
  | 'craft'
  /** 在我生活里用起来是什么样（真实使用场景） */
  | 'life'
  /** 会不会买错、合不合我的尺寸（规格 / 适配） */
  | 'spec'
  /** 和我看过的别的方案比好在哪 */
  | 'compare'
  /** 到手是什么样、配件齐不齐（配件 / 包装 / 收纳） */
  | 'package'
  /** 买错了怎么办（质保 / 服务 / 收尾） */
  | 'trust'

/**
 * 一个模块里的**一个图位 = 一次生图调用**。
 *
 * 模块与图位不是一对一：官方「Three Images & Text」是三张独立图，
 * 「Single Image & Sidebar」是 300×400 + 350×175 两种尺寸的两张。
 * 上一版模型是「一个模块 = 一张图」，遇到这些模块只能出一张，铺版填不满。
 */
export type DetailSlot = {
  /**
   * 生成尺寸。写**平台官方成品尺寸**（`970x600`）或比例（`4:5`），
   * 上游按这个比例出图；实际像素随分辨率档位等比放大
   * （实测 2K 下 970x600 → 1595x986），铺版时缩到官方宽度即可，不需要裁切。
   */
  size: string
  /**
   * 移动端专属资产的尺寸。
   *
   * 只有**高级 A+ 的全宽类模块**需要它（官方：桌面 1464×600 / 移动 600×450，
   * 两个独立文件）。比例差得远（2.44 vs 1.33），**不能拿桌面图裁**。
   * 不填 = 该图位复用桌面图（官方对小模块就是这么规定的）。
   */
  mobileSize?: string
  /** 这一格具体画什么 —— 多图模块逐格不同，进提示词 */
  role: string
}

export type DetailModule = {
  /** 模块名（界面展示 + 进提示词的「本屏任务」） */
  name: string
  /**
   * 这一屏在**买家决策链**里扮演的角色 —— 详情页的连贯性靠它串起来。
   *
   * 为什么必须有这个字段（2026-09-29 用户报障「生成提示词太傻了」）：
   * 上一版把「这一屏替买家解决什么」交给了用户 —— 出图参数里有个可编辑的
   * 「详情页模块顺序」文本框，用户写「主视觉 / 核心痛点 / 解决方案 / 产品参数…」，
   * 每个词直接变成一张图的唯一指令。三个问题因此一起出现：
   *   1. 用户手写的词命不中官方模块名 → 每屏任务退化成同一句通用模板；
   *   2. 六份提示词除模块名以外逐字相同 → 模型出六张几乎一样的图；
   *   3. 没有任何跨屏信息 → 模型不知道第 3 屏要承接第 2 屏什么，
   *      标题互相重复（真跑出来过「解决上火烦恼」/「解决方案」挨着）。
   *
   * 现在这一屏的语义由**规格层直接给出**（这里），叙事线、承接关系、
   * 视觉角色与构图尺度由 `config/detailPlan.ts` 按它派生 ——
   * 不再有第二份「模块清单」，也不再有需要用户手写的规划。
   */
  beat: DetailBeatRole
  /**
   * 单图模块的生成尺寸。多图模块改用 `slots` 覆写，这里留空。
   * ⚠️ 别在两档之间复用同一个值 —— 见文件头结论 1。
   */
  size?: string
  /** 单图模块的移动端资产尺寸（含义同 `DetailSlot.mobileSize`） */
  mobileSize?: string
  /** 这一屏替买家解决什么问题、拿什么视觉证据证明 —— 进提示词 */
  task: string
  /** 画面内不得出现任何文字（Amazon 主图这类合规硬约束） */
  textFree?: boolean
  /** 多图模块的图位清单（三图 3 格、四图 4 格、单图+侧栏 2 格）。单图模块不必写 */
  slots?: DetailSlot[]
}

export type DetailSpec = {
  /**
   * 竖版长图 = 往下翻的详情页（国内 / Ozon 这类，宽度固定、模块纵向拼）；
   * 模块化 = A+ / DTC 那类，横竖混排、每个模块自己的尺寸。
   */
  form: 'vertical-long' | 'modular'
  /** 这个形态在该平台叫什么 —— 页签直接用它，不再写死「亚马逊 A+」 */
  tabName: string
  tabDesc: string
  /**
   * 档位。只有分档的平台（Amazon：基本 A+ / 高级 A+）才有。
   * 两个档位的模块数与尺寸都不一样，**混用会错版**。
   */
  tier?: 'basic' | 'premium'
  /**
   * 该平台对**文件本身**的硬约束（不是画面内容，是文件规格）。
   * Amazon 会因此直接拒稿，所以必须进提示词与核对卡。
   */
  fileRules?: string
  /** 模块清单，顺序 = 出图顺序 */
  modules: DetailModule[]
  /** 该平台详情页的合规 / 排版硬约束，进提示词 */
  note: string
  /** 详情页入口图标（前端展示用） */
  icon?: IconName
}

/* -------------------------------------------------------------------------- */
/* 形态族                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Amazon **基本 A+** —— 所有 Brand Registry 卖家都有。
 *
 * 一个详情页最多 5 个模块、可选 14 种。这里是「通用商品」的 5 屏最优序列：
 * 开场 → 痛点 → 核心特性（三图）→ 细节信任（单图+侧栏）→ 收尾。
 *
 * ⚠️ 尺寸是 970 系，**不是** 1464 系。两档比例不同，别拿高级档的图往这里塞。
 */
const AMAZON_APLUS_BASIC: DetailSpec = {
  form: 'modular',
  tier: 'basic',
  tabName: 'Amazon A+（基本）',
  tabDesc: '基本 A+：最多 5 个模块，hero 970×600，全部卖家可用',
  icon: 'grid',
  fileRules:
    '每个文件 < 2 MB、RGB 色彩空间（CMYK 会被拒）、jpg / png / bmp、≥ 72 dpi；不接受动图（GIF）、水印、二维码、外链；图内不要塞文字（放模块自带的文字字段里，手机上才读得清）。',
  modules: [
    {
      name: 'Image Header with Text · 开场',
      beat: 'hook',
      size: '970x600',
      task: 'hero 主视觉：产品定妆 + 使用氛围，主体居右或居左，另一侧留出干净的文字安全区（文案由后台文字字段承载，画面本身不排字）',
    },
    {
      name: 'Image & Dark Text Overlay · 痛点',
      beat: 'need',
      size: '970x300',
      task: '一条横幅讲清「没有它会怎样」：同一个场景的光线氛围要暗一档，让痛点有情绪，产品作为对照出现在画面里',
    },
    {
      name: 'Three Images & Text · 核心特性',
      beat: 'proof',
      task: '三个并列卖点，每格一个视觉证据',
      slots: [
        { size: '300x300', role: '材质或工艺微距特写，主体占满画面，背景干净' },
        { size: '300x300', role: '真实使用动作特写，手部入镜体现尺度与操作性' },
        { size: '300x300', role: '结构或机制剖解，用光把关键结构从背景里挑出来' },
      ],
    },
    {
      name: 'Single Image & Sidebar · 细节信任',
      beat: 'craft',
      task: '一张竖向细节图 + 一张横向侧栏图，共同证明做工',
      slots: [
        { size: '300x400', role: '竖向细节构图：用一个具体结构件证明做工，构图饱满' },
        { size: '350x175', role: '横向侧栏图：同一材质在另一种光线下的质感，与主图呼应不重复' },
      ],
    },
    {
      name: 'Image Header with Text · 收尾',
      beat: 'trust',
      size: '970x600',
      task: '收尾主视觉：产品在真实生活场景中的全貌，光线自然、有生活气息，与开场那张换场景换机位，不复用同一构图',
    },
  ],
  note:
    '基本 A+ 一个详情页**最多 5 个模块**，hero 尺寸是 970×600（1.62:1）。想用 1464×600 的全宽版式得走高级 A+（见另一档）。主图必须纯白底且不得出现任何文字、道具、边框；A+ 图内尽量避免排字，文案交给模块的文字字段。',
}

/**
 * Amazon **高级 A+（Premium A+）** —— 需要资格（Brand Story + 5 篇已通过的
 * 基本 A+，2026 年起免费）。
 *
 * 一个详情页最多 **7 个模块**、可选 19 种。这里按买家提问顺序排成 7 屏：
 * 承诺 → 为什么需要 → 凭什么不同 → 在哪用 → 能不能信 → 为什么选这款 → 记住什么。
 *
 * 全宽类模块（1464×600）**必须另外出一张移动图 600×450**：
 * 官方是两份独立资产，比例 2.44:1 vs 4:3，拿桌面图裁会把主体切没。
 * 单图/双图模块不需要，后台复用桌面图 —— 这是 `mobileSize` 只写在部分模块上的原因。
 */
const AMAZON_APLUS_PREMIUM: DetailSpec = {
  form: 'modular',
  tier: 'premium',
  tabName: 'Amazon A+（高级）',
  tabDesc: '高级 A+：最多 7 个模块，全宽 1464×600 + 独立移动资产 600×450',
  icon: 'grid',
  fileRules:
    '每个文件 < 2 MB、RGB 色彩空间（CMYK 会被拒）、jpg / png / bmp、≥ 72 dpi；不接受动图（GIF）、水印、二维码、外链、价格与促销语。全宽类模块要**另外单独上传一张移动图**（600×450），不是把桌面图裁窄；图内文字必须 50% 缩放后仍可读，能不放就不放。',
  modules: [
    {
      name: '① 品牌开场 · Full Image',
      beat: 'hook',
      size: '1464x600',
      mobileSize: '600x450',
      task: '本产品的核心承诺：产品在 campaign 主视觉里定妆，边缘到边满铺，主体偏一侧留出文字安全区',
    },
    {
      name: '② 买家需求 · Background with Text',
      beat: 'need',
      size: '1464x600',
      mobileSize: '600x450',
      task: '把「为什么需要它」画出来：高优先级的痛点现场或期望结果，情绪要具体到场景，不要抽象氛围图',
    },
    {
      name: '③ 核心特性 · Single Image with Text',
      beat: 'proof',
      size: '800x600',
      task: '它凭什么不同：机制、结构、技术或工艺的直接证据，一个画面只证明一件事，留出短文案区（小模块，移动端复用这张，不另出）',
    },
    {
      name: '④ 场景证明 · Full Image',
      beat: 'life',
      size: '1464x600',
      mobileSize: '600x450',
      task: '真实生活里在哪用：产品在自然使用状态中，环境有生活气息且能看出尺度',
    },
    {
      name: '⑤ 细节信任 · Single Image with Text',
      beat: 'craft',
      size: '800x600',
      task: '能不能信：微距、纹理、部件或表面处理的特写，光线干净，用质感本身说服（小模块，移动端复用这张）',
    },
    {
      name: '⑥ 对比选择 · Comparison Table',
      beat: 'compare',
      task: '为什么选这个版本：本品牌 SKU 之间的对比图位，构图统一、去背同光源',
      slots: [
        { size: '488x700', mobileSize: '600x450', role: '对比表主图位（大图那一格），产品正视、背景一致' },
      ],
    },
    {
      name: '⑦ 收尾总结 · Full Image',
      beat: 'trust',
      size: '1464x600',
      mobileSize: '600x450',
      task: '购买前要记住什么：产品 + 使用结果的连贯回顾，与开场呼应但换机位换场景，画面克制不叫卖',
    },
  ],
  note:
    '高级 A+ 一个详情页**最多 7 个模块**，全宽模块 1464×600，且**要单独上传移动图 600×450**（比例 2.44:1 vs 4:3，不是裁切能解决的）。只允许本品牌商品的对比表，不许出现竞品名称/logo/价格；不得出现评价、评分、奖项（超 2 年）、联系方式、时效性促销语。',
}

/** 国内竖版详情 —— 淘宝 / 京东 / 拼多多 / 1688 / 朋友圈 等 750px 宽长图 */
const CN_VERTICAL: DetailSpec = {
  form: 'vertical-long',
  tabName: '详情页模块',
  tabDesc: '750px 宽竖版长图，一屏一个主题，可单独下载后拼接',
  icon: 'grid',
  modules: [
    {
      name: '主图 · 纯白底',
      beat: 'identity',
      size: '800x800',
      task: '纯白底正视，产品占画面 80% 以上，边缘干净，用于列表页首图',
    },
    {
      name: '详情 · 品牌主视觉',
      beat: 'hook',
      size: '750x1000',
      task: '产品 hero 构图 + 一句主标题，建立整体印象，背景与产品调性一致',
    },
    {
      name: '详情 · 核心痛点',
      beat: 'need',
      size: '750x1000',
      task: '把买家的具体麻烦画出来（旧产品/错误用法/常见困扰），产品在画面中作为对照出现',
    },
    {
      name: '详情 · 卖点拆解',
      beat: 'proof',
      size: '750x1000',
      task: '产品主体 + 3 个等距标注位，一图一卖点，每个卖点给出可验证的细节证据',
    },
    {
      name: '详情 · 材质工艺',
      beat: 'craft',
      size: '750x1000',
      task: '微距特写材质纹理、缝线或结构细节，用光线强调质感，不要整机出镜',
    },
    {
      name: '详情 · 使用场景',
      beat: 'life',
      size: '750x1000',
      task: '真实生活场景中产品正在被使用，环境有生活气息，第一人称视角更有代入感',
    },
    {
      name: '详情 · 规格参数',
      beat: 'spec',
      size: '750x1000',
      task: '产品正视 + 尺寸标线构图，留出参数表格位置（表格文字后期加，画面不要生成文字）',
    },
    {
      name: '详情 · 售后保障',
      beat: 'trust',
      size: '750x1000',
      task: '包装形态 + 配件平铺，传达「到手即用、配件齐全」的踏实感，留出服务承诺文字区',
    },
  ],
  note:
    '国内详情页以 750px 宽为基准、模块纵向拼接；一屏只讲一个主题，主标题要短（≤12 字），标题与卖点放在安全区内不压产品。',
}

/** 竖版图集型社媒 —— 小红书 / Instagram，3:4 与 4:5 为主 */
const SOCIAL_VERTICAL: DetailSpec = {
  form: 'vertical-long',
  tabName: '图集模块',
  tabDesc: '竖版图集，封面留标题位，内页走场景叙事',
  icon: 'grid',
  modules: [
    {
      name: '封面 · 标题留白',
      beat: 'hook',
      size: '1080x1440',
      task: '成品感封面构图，上半或一侧留出标题安全区，画面干净有审美吸引力（文字后期加）',
    },
    {
      name: '图集 · 真实使用场景',
      beat: 'life',
      size: '1080x1440',
      task: '生活场景里产品正在被使用，自然光、不过度修图，像真人随手拍的',
    },
    {
      name: '图集 · 细节特写',
      beat: 'craft',
      size: '1080x1440',
      task: '材质与工艺微距，柔和色调，突出手感与质感',
    },
    {
      name: '图集 · 对比/合集',
      beat: 'compare',
      size: '1080x1440',
      task: '使用前后或同类替代方案对比，构图对称、信息一眼可读',
    },
    {
      name: '图集 · 搭配/融入',
      beat: 'life',
      size: '1080x1440',
      task: '产品融入一整套生活场景（桌面/穿搭/家居），体现风格适配',
    },
    {
      name: '图集 · 收纳/包装',
      beat: 'package',
      size: '1080x1440',
      task: '包装与收纳形态，传达「好收好带」，色调与全组一致',
    },
  ],
  note:
    '社媒图 KPI 是「让人想 Save」而不是「一眼看清参数」：图内文字压到 15% 以内、英文短句 ≤6 词，卖点走场景叙事。',
}

/** 竖版详情（3:4）—— Ozon 与抖音/快手这类竖屏电商 */
const VERTICAL_34: DetailSpec = {
  form: 'vertical-long',
  tabName: '详情页模块',
  tabDesc: '3:4 竖版模块，主图 + 场景 + 卖点 + 细节 + 规格',
  icon: 'grid',
  modules: [
    { name: '主图 · 白底', beat: 'identity', size: '1080x1440', task: '纯白底正视，产品占画面 80% 以上（女装/童装/鞋/裤类目主图不要文案）' },
    { name: '图 2 · 场景使用', beat: 'life', size: '1080x1440', task: '真实使用场景，环境明亮，产品处于自然状态' },
    { name: '图 3 · 卖点图解', beat: 'proof', size: '1080x1440', task: '产品主体 + 三个等距标注位，一图一卖点，留出文字区（画面不排字）' },
    { name: '图 4 · 细节特写', beat: 'craft', size: '1080x1440', task: '材质与工艺微距，光线干净，强调质感' },
    { name: '图 5 · 尺寸规格', beat: 'spec', size: '1080x1440', task: '正视 + 尺寸标线，留出尺码表位置' },
    { name: '图 6 · 配件/包装', beat: 'package', size: '1080x1440', task: '配件平铺或包装形态，传达到手足量' },
    { name: '图 7 · 对比说明', beat: 'compare', size: '1080x1440', task: '与常见替代品或旧款的横向对比，构图对称' },
    { name: '图 8 · 售后质保', beat: 'trust', size: '1080x1440', task: '质保与服务信息位，画面稳重可信，留出文字区' },
  ],
  note: '主图文字按品类决定：女装 / 童装 / 宠物 / 鞋 / 裤主图不要文案；文案语言必须与平台语言一致，不留中文。',
}

/** 方图电商 —— Temu / Shopee / 速卖通 / TikTok Shop，1:1 为主 */
const SQUARE_SET: DetailSpec = {
  form: 'vertical-long',
  tabName: '详情页模块',
  tabDesc: '1:1 方图模块，主图 + 卖点 + 场景 + 规格 + 对比',
  icon: 'grid',
  modules: [
    { name: '主图 · 白底', beat: 'identity', size: '1000x1000', task: '纯白底正视，产品占画面 85% 以上，边缘干净，用于列表首图' },
    { name: '卖点图 · 大字位', beat: 'proof', size: '1000x1000', task: '产品主体 + 一条核心卖点，留出大号文字安全区（画面不排字）' },
    { name: '场景图', beat: 'life', size: '1000x1000', task: '真实使用场景，环境明亮有生活气，产品处于自然使用状态' },
    { name: '规格图', beat: 'spec', size: '1000x1000', task: '正视 + 尺寸标线，留出规格表格位置' },
    { name: '对比图', beat: 'compare', size: '1000x1000', task: '使用前后或与替代方案的对比，构图对称、信息直给' },
    { name: '细节图', beat: 'craft', size: '1000x1000', task: '材质与工艺微距，强调做工' },
    { name: '配件图', beat: 'package', size: '1000x1000', task: '配件平铺，传达到手即用、配件齐全' },
    { name: '包装图', beat: 'package', size: '1000x1000', task: '包装形态，像一个可以送人的成品' },
  ],
  note: '方图电商以列表页一眼看清产品为第一优先，主图不放文字（或只放极短的英文标签），信息量集中在卖点图与规格图。',
}

/** DTC / 独立站 —— Shopify，PDP 方图 + 横版内容模块混排 */
const DTC_MODULAR: DetailSpec = {
  form: 'modular',
  tabName: 'DTC 模块详情',
  tabDesc: 'PDP 方图 + 横版内容模块，按品牌调性铺版',
  icon: 'grid',
  modules: [
    { name: 'PDP · 主图', beat: 'identity', size: '2048x2048', task: '干净的品牌感产品定妆，浅色或与品牌色协调的背景，产品占画面主体' },
    { name: 'Hero · 品牌首屏', beat: 'hook', size: '1920x1080', task: '品牌主张 + 产品，大量留白，生活方式摄影质感，一侧留出标题区' },
    { name: 'PDP · 场景生活图', beat: 'life', size: '2048x2048', task: '产品真实融入生活场景，低饱和自然光，像杂志内页' },
    { name: '功能模块 · 横版', beat: 'proof', size: '1920x1080', task: '一个核心价值点配一个视觉证据，构图克制，留出短文案区' },
    { name: 'PDP · 细节特写', beat: 'craft', size: '2048x2048', task: '材质与工艺微距，突出手感与做工，光线干净' },
    { name: '内容模块 · 材质故事', beat: 'craft', size: '1920x1080', task: '原料/工艺的产地感画面，传达为什么这样做，氛围优先' },
    { name: '内容模块 · 尺码与规格', beat: 'spec', size: '1920x1080', task: '规格以极简排版呈现，留出参数区，不要参数堆成表格墙' },
    { name: '信任背书 · 横版', beat: 'trust', size: '1920x1080', task: '包装、质保、服务承诺的视觉化呈现，稳重不叫卖' },
  ],
  note: 'DTC 详情不叫卖：避免电商平台式的大字促销排版，文案 en-US、短句、留白充足，重心是品牌调性与生活方式摄影。',
}

/* -------------------------------------------------------------------------- */
/* 族 id → 规格（platforms.ts 挂引用用）                                        */
/* -------------------------------------------------------------------------- */

export const DETAIL_SPECS = {
  AMAZON_APLUS_BASIC,
  AMAZON_APLUS_PREMIUM,
  CN_VERTICAL,
  SOCIAL_VERTICAL,
  VERTICAL_34,
  SQUARE_SET,
  DTC_MODULAR,
} as const

export type DetailSpecId = keyof typeof DETAIL_SPECS

/* -------------------------------------------------------------------------- */
/* 派生（不手写第二份清单）                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 模块 → 图位清单。
 *
 * 单图模块不必写 `slots`，这里补成 1 格 —— 避免「有的模块写 slots、有的写 size」
 * 两套写法，调用方只需认 `moduleSlots()` 一个入口。
 */
export function moduleSlots(m: DetailModule): DetailSlot[] {
  if (m.slots?.length) return m.slots
  return [{ size: m.size ?? '1:1', mobileSize: m.mobileSize, role: '' }]
}

/** 规格 → 全部图位（顺序 = 出图顺序，与官方模块顺序一致） */
export function specSlots(spec: DetailSpec): DetailSlot[] {
  return spec.modules.flatMap(moduleSlots)
}

/**
 * 该档位下的**总生图次数**。
 *
 * `withMobile` 打开时，凡是官方要求独立移动资产的图位都要**再多出一张**
 * —— 注意不是每个图位都有：只有全宽类（1464×600）与对比表大图才有，
 * 单图/双图/四图模块官方是复用桌面图，所以这里按 `mobileSize` 逐位累加，
 * 不能简单地「张数 × 2」。
 */
export function slotCount(spec: DetailSpec, withMobile: boolean): number {
  const slots = specSlots(spec)
  return slots.length + (withMobile ? slots.filter((s) => s.mobileSize).length : 0)
}

/**
 * 官方模块的**份数上限** —— 一个模块最多来几轮。
 *
 * 上限存在的理由不是防误输（数字框也能防），而是**这一维本来就没有意义再大**：
 * 同一个模块出 4 轮以上，模型给不出第 5 种构图，只会把前几张换个角度重画。
 * 与 `MAX_BATCH_COUNT`（整批张数上限）是两把尺子，别互相替代。
 */
export const MODULE_MAX_TIMES = 3

/**
 * 一个模块**每份出几张**。
 *
 * ⚠️ 「份数」与「张数」不是一个量，这条函数就是那条换算：
 *   · 官方多图模块（A+ 三图 / 单图 + 侧栏）一个模块顶 3 张、2 张；
 *   · `withMobile` 打开后，凡官方要求独立移动资产的图位还要**再加一张**。
 * 页面上的 ± 调的是**份数**（这个模块来几轮），显示的是**张数**。
 * 把两者当同一个，用户勾一个三图模块只会拿到 1 张 —— 而且不报错，
 * 只是逐张清单里少两行，验收时很难发现（2026-10-07 那版的 `moduleSlotCount`
 * 就是为同一件事写的，这里是它的逐模块版本）。
 */
export function moduleShotCount(module: DetailModule, withMobile = false): number {
  const slots = moduleSlots(module)
  return slots.length + (withMobile ? slots.filter((s) => s.mobileSize).length : 0)
}

/**
 * 该规格下**实际会用到的生成尺寸清单**（去重，保持首次出现顺序）。
 *
 * 用途：aplus 页签要把「这次一共会出哪几种尺寸」摊给用户看 ——
 * 之前这里只有一个 16:9 的下拉，选什么都对不上 A+ 的模块规格。
 * 现在不给选，直接告诉他每一张的真实尺寸。
 */
export function detailSizes(spec: DetailSpec): string[] {
  return [...new Set(specSlots(spec).map((s) => s.size))]
}

/** 需要**独立移动资产**的尺寸对（桌面 `size` → 移动 `mobileSize`） */
export function mobilePairs(spec: DetailSpec): { size: string; mobileSize: string }[] {
  const seen = new Set<string>()
  const out: { size: string; mobileSize: string }[] = []
  for (const s of specSlots(spec)) {
    if (!s.mobileSize) continue
    const key = `${s.size}->${s.mobileSize}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ size: s.size, mobileSize: s.mobileSize })
  }
  return out
}

/**
 * 尺寸的展示写法：`970x600` → `970×600`。
 *
 * 存的是半角 x（要直接透传给上游 `size` 参数，不能带全角乘号），
 * 给人看的时候换成乘号。**这是一次格式化，不是第二份数据。**
 */
export function displaySize(size: string): string {
  return size.replace(/x/gi, '×')
}

/**
 * 尺寸在界面上的写法 —— 像素写 `970×600`，比例原样写（`1:1`）。
 *
 * **唯一一份**，本来长在 `lib/detailPrompts.ts` 里。上提是因为
 * `config/studioShape.ts` 的尺寸选择器也要它 —— 那边 import 提示词模块会成环
 * （`detailPrompts` 反过来 import `studioShape` 的 `Shot` 类型），
 * 而各写一份的结果是「同一个尺寸在两个控件里长得不一样」。
 */
export function sizeLabel(size: string): string {
  return isPixelSize(size) ? displaySize(size) : size
}

/** 尺寸是否带具体像素（`970x600`）而不是比例（`1:1`） */
export function isPixelSize(size: string): boolean {
  return /\d+x\d+/i.test(size)
}

/**
 * 该尺寸在中文里的口头说法 —— 只用于界面提示，不参与生成。
 * `970x600` → `约 1.62:1（16:9 是 1.78:1，不等同）`
 */
export function ratioHint(size: string): string | undefined {
  const m = /^(\d+)x(\d+)$/i.exec(size.trim())
  if (!m) return undefined
  const w = Number(m[1])
  const h = Number(m[2])
  if (!w || !h) return undefined
  return `${(w / h).toFixed(2)}:1`
}
